import React from 'react';
import { Box, Typography, Divider } from '@mui/material';
import { numberToWords } from '../utils/numberToWords';
import { Fornecedor, Investidor, Unidade, Obra } from '../services/types';

export interface Pagador {
    id: number;
    nome: string;
    documento: string;
    tipo: 'Investidor' | 'Fornecedor';
}

export interface ReciboItem {
    id?: string;
    descricao: string;
    quantidade: number | string;
    valor: number;
}

interface ReciboPrintProps {
    fornecedor: Fornecedor;
    investidor: Investidor;
    unidade?: Unidade | null;
    obra?: Obra | null;
    valor: number;
    data: Date;
    descricao: string;
    cidade: string;
    estado: string;
    tipoDocumentoBeneficiario: 'CPF' | 'CNPJ';
    pagador?: Pagador | null;
    quitaIntegralmente?: boolean;
    tipoRecibo?: 'servico' | 'material';
    itens?: ReciboItem[];
}

// Format currency
const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
};

// Format date for text (e.g., "31 de outubro de 2025")
const formatDateFull = (date: Date) => {
    return date.toLocaleDateString('pt-BR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
    });
};

const ReciboPrint = React.forwardRef<HTMLDivElement, ReciboPrintProps>((props, ref) => {
    const {
        fornecedor,
        investidor,
        unidade,
        obra,
        valor,
        data,
        descricao,
        cidade,
        tipoDocumentoBeneficiario,
        pagador,
        quitaIntegralmente,
        tipoRecibo = 'servico',
        itens = []
    } = props;

    // Construct text parts
    const valorExtenso = numberToWords(valor);
    const documentoLabel = tipoDocumentoBeneficiario; // 'CPF' or 'CNPJ'

    // Address text logic
    const getEndereco = () => {
        if (obra) {
            return `${obra.endereco || ''}${obra.municipio ? `, ${obra.municipio}` : ''}${obra.estado ? ` - ${obra.estado}` : ''}`;
        }
        if (unidade) {
            return `${unidade.endereco || ''}${unidade.bairro ? `, ${unidade.bairro}` : ''}${unidade.municipio ? `, ${unidade.municipio}` : ''}-${unidade.estado || ''}`;
        }
        return 'Endereço não especificado';
    };

    const enderecoImovel = getEndereco();
    
    const isMaterialWithItens = tipoRecibo === 'material' && itens.length > 0;

    const textoReferente = isMaterialWithItens
        ? `dos materiais de construção do terreno localizado na ${enderecoImovel}, a seguir:`
        : tipoRecibo === 'material' 
            ? `pelo Fornecimento de ${descricao} entregues na ${enderecoImovel}.` 
            : `pelo Serviço de ${descricao} localizado na ${enderecoImovel}.`;

    const textoQuitacao = tipoRecibo === 'material'
        ? `O presente pagamento quita integralmente o valor acordado pelos materiais mencionados.`
        : `O presente pagamento quita integralmente o valor acordado pelo serviço mencionado.`;

    return (
        <Box
            ref={ref}
            sx={{
                p: { xs: 4, sm: 8 },
                bgcolor: 'white',
                color: 'black',
                maxWidth: '800px',
                margin: '0 auto',
                fontFamily: '"Times New Roman", Times, serif',
                fontSize: '12pt',
                lineHeight: 1.6,
                '@media print': {
                    p: '0 !important',
                    width: '100% !important',
                    maxWidth: 'none !important',
                    margin: '0 !important'
                }
            }}
        >
            <Typography variant="h5" align="center" sx={{ fontWeight: 'bold', mb: 4, textTransform: 'uppercase', fontSize: '14pt' }}>
                RECIBO DE PAGAMENTO
            </Typography>

            <Typography variant="h6" sx={{ mb: 3, fontWeight: 'normal', fontSize: '12pt' }}>
                Valor: {formatCurrency(valor)}
            </Typography>

            <Typography paragraph sx={{ textAlign: 'justify', mb: 3, fontSize: '12pt' }}>
                Eu, <strong>{fornecedor.nome_razao_social.toUpperCase()}</strong>, inscrito no {documentoLabel} sob o nº {fornecedor.cnpj_cpf},
                declaro para os devidos fins que recebi de <strong>{investidor.nome.toUpperCase()}</strong>,
                {pagador ? (
                    <> pago por <strong>{pagador.nome.toUpperCase()}</strong>, inscrito no {pagador.documento.length > 14 ? 'CNPJ' : 'CPF'} sob o nº {pagador.documento}, </>
                ) : null}
                {' '}a quantia de <strong>{formatCurrency(valor)} ({valorExtenso})</strong>.
                Este valor é referente ao pagamento {textoReferente}
            </Typography>

            {isMaterialWithItens && (
                <Box sx={{ mb: 4 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', border: '2px solid black', fontFamily: '"Times New Roman", Times, serif', fontSize: '12pt' }}>
                        <thead>
                            <tr>
                                <th style={{ border: '1px solid black', padding: '8px', textAlign: 'center' }}>Descrição</th>
                                <th style={{ border: '1px solid black', padding: '8px', textAlign: 'center' }}>Quantidade</th>
                                <th style={{ border: '1px solid black', padding: '8px', textAlign: 'center' }}>Valor</th>
                            </tr>
                        </thead>
                        <tbody>
                            {itens.map((item, index) => (
                                <tr key={item.id || index}>
                                    <td style={{ border: '1px solid black', padding: '8px', textAlign: 'center' }}>{item.descricao}</td>
                                    <td style={{ border: '1px solid black', padding: '8px', textAlign: 'center' }}>{item.quantidade}</td>
                                    <td style={{ border: '1px solid black', padding: '8px', textAlign: 'center' }}>{formatCurrency(item.valor)}</td>
                                </tr>
                            ))}
                            <tr>
                                <td colSpan={2} style={{ border: '1px solid black', padding: '8px', textAlign: 'center', fontWeight: 'bold' }}>Total</td>
                                <td style={{ border: '1px solid black', padding: '8px', textAlign: 'center' }}>{formatCurrency(valor)}</td>
                            </tr>
                        </tbody>
                    </table>
                </Box>
            )}

            {quitaIntegralmente && (
                <Typography paragraph sx={{ textAlign: 'justify', mb: 4, fontSize: '12pt' }}>
                    {textoQuitacao}
                </Typography>
            )}

            <Typography paragraph sx={{ mb: 8, fontSize: '12pt' }}>
                {cidade}, {formatDateFull(data)}.
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 4 }}>
                <Divider sx={{ width: '60%', bgcolor: 'black', mb: 1, height: '1px' }} />
                <Typography align="center" sx={{ fontWeight: 'bold', fontSize: '12pt' }}>
                    {fornecedor.nome_razao_social.toUpperCase()}
                </Typography>
                <Typography align="center" sx={{ fontSize: '12pt' }}>
                    {documentoLabel}: {fornecedor.cnpj_cpf}
                </Typography>
            </Box>
        </Box>
    );
});

export default ReciboPrint;
